import { Events, Fields, Versions } from '@vimeo/bigpicture-client';
import getDeviceType from '@vimeo/ui/core/utils/getDeviceType';
import {
  buildActionBpContext,
  buildCommentBpContext,
  buildProductAnalyticsBpContext,
  buildTeamBpContextFromTeam,
  buildTeamBpContextFromTeamUser,
  buildThirdPartyIntegrationBpContext,
  buildVideoBpContext,
  buildVideoViewBpContext,
  buildWebBpContext,
  buildFolderBpContext,
  sendBpEventWithContexts,
  CommentBpContext,
  WebBpContext,
  ThirdPartyIntegrationBpContext,
} from '@vimeo/use-tracking';
import { Viewer } from '@vimeo/viewer';

import { AnalyticsPropsType } from '../CommentsCommon/types';
import { type VideoPrivacy } from '../utils/constants';
import {
  getUserIdFromUri,
  idFromUri,
  isInternalFacingPrivacy,
  getUserIdFromUserLink,
} from '../utils/helper';

const COMMENT_ACTION_GROUP_EVENT_VERSION = 13;

const defaultActionContext = () => {
  return buildActionBpContext({
    action_type: 'click',
    feature: null,
  });
};

const defaultWebContext = (webContextFields?: Partial<WebBpContext>) => {
  return buildWebBpContext({
    page_name: webContextFields?.page_name as WebBpContext['page_name'],
    path: window.location.pathname,
    ...(document.referrer && { referrer: document.referrer }),
    ...webContextFields,
  });
};

const defaultThirdPartyIntegrationContext = () => {
  return buildThirdPartyIntegrationBpContext({
    is_integration: false,
    integration_id: null,
    integration_name: null,
    managed_user_id: null,
    app_id: null,
    integration_type: null,
    partner_bucket: null,
    is_partner: null,
  });
};

const buildDefaultThirdPartyIntegrationBpContext = (
  thirdPartyContextFields: Partial<ThirdPartyIntegrationBpContext> = {},
) => {
  return buildThirdPartyIntegrationBpContext({
    is_integration: false,
    integration_id: null,
    integration_name: null,
    managed_user_id: null,
    app_id: null,
    integration_type: null,
    partner_bucket: null,
    is_partner: null,
    ...thirdPartyContextFields,
  });
};

const buildDefaultCommentsContext = (commentContextFields: Partial<CommentBpContext>) => {
  const defaultCommentContextFields = {
    comment_length: null,
    comment_owner_id: 0,
    existing_reply_counts: null,
    allow_comment_toggle: null,
    is_mention: null,
    mentionee_email: null,
    mentionee_permission: null,
    mentionee_id: null,
    is_pending: null,
  };

  return buildCommentBpContext({
    ...defaultCommentContextFields,
    ...commentContextFields,
  } as CommentBpContext);
};

const defaultVideoViewerContext = (sessionId: string) => {
  return buildVideoViewBpContext({
    viewer_home_session_id: sessionId,
  });
};

const buildDefaultContext = (webContextFields?: Partial<WebBpContext>, viewer?: Viewer | null) => {
  const actionContext = defaultActionContext();
  const webContext = defaultWebContext(webContextFields);
  const teamContext = viewer?.teamUser
    ? buildTeamBpContextFromTeamUser(viewer?.teamUser)
    : buildTeamBpContextFromTeam(viewer?.team);

  return {
    ...actionContext,
    ...webContext,
    ...teamContext,
  };
};

export const bpProfileLink = (
  videoId: string,
  sessionId: string,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
) => {
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);
  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'viewer_home',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'button',
    copy: null,
    device_type: getDeviceType(),
    ...analyticsProps?.productContextFields,
  });

  const videoViewContext = defaultVideoViewerContext(sessionId);

  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...videoViewContext,
    ...commentContext,
  };

  sendBpEventWithContexts('vimeo.select_user_profile_button', contexts);
};

export const bpDeleteComment = (
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  isPublic: boolean,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
  isTimeCoded?: boolean | null,
  uploaderLink?: string | undefined,
) => {
  const curUserId = viewer?.user?.id || -1;
  const uploaderId = getUserIdFromUserLink(uploaderLink || '');
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'ellipses',
    copy: 'Delete',
    entity_type: 'video',
    device_type: getDeviceType(),
    is_user_facing_data: false,
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  const isLoggedOut = !viewer?.user?.uri;

  sendBpEventWithContexts(
    'vimeo.delete_comment',
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION as Versions<Events>,
    {
      posted_by_uploader: curUserId === uploaderId,
      posted_by_guest: isLoggedOut,
      is_timecoded: isTimeCoded,
      is_internal: !isPublic,
      value: null,
      option: null,
      comments_format: null,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpEditComment = (
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  isPublic: boolean,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
  uploaderLink?: string | undefined,
  isTimeCoded: boolean = false,
) => {
  const curUserId = viewer?.user?.id || 0;
  const uploaderId = getUserIdFromUserLink(uploaderLink || '');
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'ellipses',
    copy: 'Edit',
    device_type: getDeviceType(),
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};

  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  const isLoggedOut = !viewer?.user?.uri;

  sendBpEventWithContexts('vimeo.edit_comment', contexts, COMMENT_ACTION_GROUP_EVENT_VERSION, {
    posted_by_uploader: curUserId === uploaderId,
    posted_by_guest: isLoggedOut,
    is_timecoded: isTimeCoded,
    is_internal: !isPublic,
    value: null,
    option: null,
    comments_format: null,
  } as Fields<Events, Versions<Events>>);
};

export const bpPostComment = (
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  uploaderId: number,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
  isTimeCoded: boolean = false,
  isPublic: boolean = true,
) => {
  const curUserId = viewer?.user?.id || 0;
  const isInternal = !!videoPrivacy && isInternalFacingPrivacy(videoPrivacy);

  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'button',
    copy: 'Comment',
    entity_type: 'video',
    device_type: getDeviceType(),
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  const isLoggedOut = !viewer?.user?.uri;

  sendBpEventWithContexts(
    'vimeo.post_comment',
    contexts,
    6 as Versions<Events>,
    {
      posted_by_uploader: curUserId === uploaderId,
      posted_by_guest: isLoggedOut,
      is_timecoded: isTimeCoded,
      is_internal: !isPublic,
      value: null,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpCopyCommentLink = (
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  uploaderLink: string | undefined,
  isPublic: boolean,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
  isTimeCoded?: boolean,
) => {
  const curUserId = viewer?.user?.id || 0;
  const uploaderId = Number((uploaderLink || '').match(/user(\d+)/)?.[1] || 0);

  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'ellipses',
    copy: 'Copy link to comment',
    entity_type: 'video',
    is_user_facing_data: false,
    device_type: getDeviceType(),
  });

  const thirdPartyIntegrationContext = defaultThirdPartyIntegrationContext();

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};

  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts('vimeo.copy_comment_link', contexts, COMMENT_ACTION_GROUP_EVENT_VERSION, {
    posted_by_uploader: curUserId === uploaderId,
    posted_by_guest: !curUserId,
    is_timecoded: isTimeCoded,
    is_internal: !isPublic,
    value: null,
    option: null,
    comments_format: null,
  } as Fields<Events, Versions<Events>>);
};

export const bpOpenCommentOptions = (
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  uploaderLink: string | undefined,
  isPublic: boolean,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
  isTimeCoded?: boolean,
) => {
  const curUserId = viewer?.user?.id || 0;
  const uploaderId = Number((uploaderLink || '').match(/user(\d+)/)?.[1] || 0);

  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'ellipses',
    copy: null,
    entity_type: 'video',
    is_user_facing_data: false,
    device_type: getDeviceType(),
  });

  const thirdPartyIntegrationContext = defaultThirdPartyIntegrationContext();

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts(
    'vimeo.open_comment_options',
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION,
    {
      posted_by_uploader: curUserId === uploaderId,
      posted_by_guest: !curUserId,
      is_timecoded: isTimeCoded,
      is_internal: !isPublic,
      value: null,
      option: null,
      comments_format: null,
    } as Fields<Events, Versions<Events>>,
  );
};
type BpOpenCommentsProps = {
  videoId: string;
  isOpen: boolean;
  isPublic: boolean;
  analyticsProps: AnalyticsPropsType;
  viewer?: Viewer;
};

export const bpOpenComments = ({
  videoId,
  isOpen,
  isPublic,
  viewer,
  analyticsProps,
}: BpOpenCommentsProps) => {
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    ...analyticsProps?.videoContextFields,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'top_action_bar',
    element: 'icon',
    is_user_facing_data: false,
    entity_type: 'video',
    device_type: getDeviceType(),
    ...analyticsProps?.productContextFields,
  });

  const folderContext = analyticsProps?.folderContextFields
    ? buildFolderBpContext({
        folder_id: analyticsProps?.folderContextFields?.folder_id ?? 0,
        ...analyticsProps?.folderContextFields,
      })
    : {};

  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...folderContext,
  };

  sendBpEventWithContexts(
    'vimeo.open_comments',
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION as Versions<Events>,
    {
      posted_by_uploader: null,
      posted_by_guest: null,
      is_timecoded: null,
      is_internal: !isPublic,
      value: isOpen,
      option: null,
      comments_format: null,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpMentionImpression = (
  videoId: number,
  videoPrivacy: VideoPrivacy | undefined = 'nobody',
  analyticsProps: AnalyticsPropsType,
  isPublic: boolean,
  viewer?: Viewer,
) => {
  const curUserId = viewer?.user?.id || 0;

  const webContext = defaultWebContext(analyticsProps.webContextFields);
  const teamContext = viewer?.teamUser
    ? buildTeamBpContextFromTeamUser(viewer?.teamUser)
    : buildTeamBpContextFromTeam(viewer?.team);

  const videoContext = buildVideoBpContext({
    video_id: videoId,
    video_privacy: videoPrivacy,
  });

  const actionContext = buildActionBpContext({
    action_type: 'type',
    feature: null,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'button',
    copy: null,
    device_type: getDeviceType(),
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = defaultThirdPartyIntegrationContext();

  const commentContext = buildDefaultCommentsContext({
    comment_owner_id: curUserId,
    allow_comment_toggle: true,
    is_mention: true,
    ...analyticsProps?.commentContextFields,
  });

  const contexts = {
    ...actionContext,
    ...productAnalyticsContext,
    ...thirdPartyIntegrationContext,
    ...videoContext,
    ...commentContext,
    ...teamContext,
    ...webContext,
  };

  sendBpEventWithContexts(
    'vimeo.mention_box_impression',
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION as Versions<Events>,
    {
      posted_by_uploader: null,
      posted_by_guest: null,
      is_timecoded: null,
      is_internal: !isPublic,
      value: null,
      option: null,
      comments_format: null,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpAddMentionee = (
  videoId: number,
  videoPrivacy: VideoPrivacy | undefined = 'nobody',
  uploaderLink: string | undefined,
  isPublic: boolean,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer,
) => {
  const curUserId = viewer?.user?.id || 0;
  const uploaderId = Number((uploaderLink || '').match(/user(\d+)/)?.[1] || 0);

  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: videoId,
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'text',
    copy: null,
    entity_type: 'video',
    device_type: getDeviceType(),
    is_user_facing_data: false,
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = defaultThirdPartyIntegrationContext();

  const commentContext = buildDefaultCommentsContext({
    comment_id: 0,
    comment_owner_id: curUserId,
    allow_comment_toggle: true,
    is_mention: true,
    ...analyticsProps?.commentContextFields,
  });

  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts(
    'vimeo.add_mentionee_to_comment',
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION,
    {
      posted_by_uploader: curUserId === uploaderId,
      posted_by_guest: !curUserId,
      is_timecoded: null,
      is_internal: !isPublic,
      value: null,
      option: null,
      comments_format: null,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpSortComment = (
  sortOption: string,
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  uploaderLink: string | undefined,
  isPublic: boolean,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
) => {
  const curUserId = viewer?.user?.id || 0;
  const uploaderId = Number((uploaderLink || '').match(/user(\d+)/)?.[1] || 0);

  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'dropdown',
    copy: sortOption,
    device_type: getDeviceType(),
    entity_type: 'video',
    is_user_facing_data: false,
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts('vimeo.sort_comment', contexts, COMMENT_ACTION_GROUP_EVENT_VERSION, {
    posted_by_uploader: curUserId === uploaderId,
    posted_by_guest: !curUserId,
    is_timecoded: false,
    is_internal: !isPublic,
    value: null,
    option: sortOption,
    comments_format: null,
  } as Fields<Events, Versions<Events>>);
};

export const bpSaveEditComment = (
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  isPublic: boolean,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
  isTimeCoded?: boolean,
  uploaderLink?: string | undefined,
  option: string | undefined = 'text',
) => {
  const curUserId = viewer?.user?.id || 0;
  const uploaderId = Number((uploaderLink || '').match(/user(\d+)/)?.[1] || 0);
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'button',
    copy: 'Save',
    device_type: getDeviceType(),
    entity_type: 'video',
    is_user_facing_data: false,
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  const isLoggedOut = !viewer?.user?.uri;

  sendBpEventWithContexts(
    'vimeo.post_edited_comment',
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION,
    {
      posted_by_uploader: curUserId === uploaderId,
      posted_by_guest: isLoggedOut,
      is_timecoded: !!isTimeCoded,
      is_internal: !isPublic,
      value: null,
      option,
      comments_format: null,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpResolveComment = (
  commentResolved: boolean,
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
  isTimeCoded?: boolean,
  uploaderLink?: string | undefined,
) => {
  const curUserId = viewer?.user?.id || 0;
  const uploaderId = Number((uploaderLink || '').match(/user(\d+)/)?.[1] || 0);
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'check_box',
    copy: null,
    device_type: getDeviceType(),
    entity_type: 'video',
    is_user_facing_data: false,
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  const isLoggedOut = !viewer?.user?.uri;

  sendBpEventWithContexts('vimeo.resolve_comment', contexts, COMMENT_ACTION_GROUP_EVENT_VERSION, {
    posted_by_uploader: curUserId === uploaderId,
    posted_by_guest: isLoggedOut,
    is_timecoded: !!isTimeCoded,
    is_internal: true,
    value: commentResolved,
    option: null,
    comments_format: null,
  } as Fields<Events, Versions<Events>>);
};

export const bpOpenCommentsTab = (
  copy: string,
  isPublic: boolean,
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
) => {
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'sub_menu',
    copy: copy,
    device_type: getDeviceType(),
    entity_type: 'video',
    is_user_facing_data: false,
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts('vimeo.open_comments_tab', contexts, COMMENT_ACTION_GROUP_EVENT_VERSION, {
    posted_by_uploader: null,
    posted_by_guest: null,
    is_timecoded: null,
    is_internal: !isPublic,
    value: null,
    option: null,
    comments_format: null,
  } as Fields<Events, Versions<Events>>);
};

export const bpToggleShowResolvedComments = (
  toggleStatus: boolean,
  videoId: string,
  videoPrivacy: VideoPrivacy | undefined,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
) => {
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'button',
    copy: 'Resolved',
    device_type: getDeviceType(),
    entity_type: 'video',
    is_user_facing_data: false,
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts(
    'vimeo.toggle_show_resolved_comments',
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION,
    {
      posted_by_uploader: null,
      posted_by_guest: null,
      is_timecoded: null,
      is_internal: true,
      value: toggleStatus,
      option: null,
      comments_format: null,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpFilterComments = (
  filterOption: string,
  copy: string,
  videoId: string,
  isPublic: boolean,
  videoPrivacy: VideoPrivacy | undefined,
  analyticsProps: AnalyticsPropsType,
  viewer?: Viewer | null,
) => {
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'button',
    copy: copy,
    device_type: getDeviceType(),
    entity_type: 'video',
    is_user_facing_data: false,
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts('vimeo.filter_comments', contexts, COMMENT_ACTION_GROUP_EVENT_VERSION, {
    posted_by_uploader: null,
    posted_by_guest: null,
    is_timecoded: null,
    is_internal: !isPublic,
    value: null,
    option: filterOption,
    comments_format: null,
  } as Fields<Events, Versions<Events>>);
};

export const bpStartComment = (
  videoId: number,
  videoPrivacy: VideoPrivacy | undefined,
  isPublic: boolean,
  analyticsProps: AnalyticsPropsType,
  uploaderLink: string | undefined,
  element: string = 'text',
  isTimeCoded?: boolean,
  viewer?: Viewer | null,
) => {
  const curUserId = viewer?.user?.id || 0;
  const uploaderId = Number((uploaderLink || '').match(/user(\d+)/)?.[1] || 0);

  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: videoId,
    video_privacy: videoPrivacy,
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: element as 'text' | 'ellipses',
    copy: null,
    device_type: getDeviceType(),
    entity_type: 'video',
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  const isLoggedOut = !viewer?.user?.uri;

  sendBpEventWithContexts(
    'vimeo.start_comment',
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION as Versions<Events>,
    {
      posted_by_uploader: curUserId === uploaderId,
      posted_by_guest: isLoggedOut,
      is_timecoded: isTimeCoded,
      is_internal: !isPublic,
      value: null,
      option: null,
      comments_format: null,
    } as Fields<Events, Versions<Events>>,
  );
};

export const bpAllowViewerComments = (
  isAllowed: boolean,
  copy: string,
  videoId: string,
  isPublic: boolean,
  videoPrivacy: VideoPrivacy | undefined,
  analyticsProps: AnalyticsPropsType,
  videoOwnerUri?: string,
  viewer?: Viewer | null,
) => {
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);

  const videoContext = buildVideoBpContext({
    video_id: parseFloat(videoId),
    video_privacy: videoPrivacy,
    video_owner_id: getUserIdFromUri(videoOwnerUri ?? ''),
  });

  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    modal_name: null,
    flow: null,
    element: 'dropdown',
    copy: copy,
    device_type: getDeviceType(),
    entity_type: 'video',
    is_user_facing_data: false,
    ...analyticsProps?.productContextFields,
  });

  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const commentContext = analyticsProps?.commentContextFields
    ? buildDefaultCommentsContext(analyticsProps?.commentContextFields)
    : {};
  const contexts = {
    ...defaultContext,
    ...videoContext,
    ...productAnalyticsContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  sendBpEventWithContexts(
    'vimeo.allow_viewer_comments',
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION,
    {
      posted_by_uploader: null,
      posted_by_guest: null,
      is_timecoded: null,
      is_internal: false,
      value: isAllowed,
      option: null,
      comments_format: null,
    },
  );
};

type bpStartSearchCommentProps = {
  isInternal: boolean;
  videoId: number;
  videoPrivacy?: VideoPrivacy;
  viewer?: Viewer;
  analyticsProps?: AnalyticsPropsType;
};
export const bpStartSearchComment = ({
  isInternal,
  videoId,
  videoPrivacy,
  viewer,
  analyticsProps,
}: bpStartSearchCommentProps) => {
  const defaultContext = buildDefaultContext(analyticsProps?.webContextFields, viewer);
  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    element: 'icon',
    entity_type: 'video',
    ...analyticsProps?.productContextFields,
  });
  const videoContext = buildVideoBpContext({
    video_id: videoId,
    video_privacy: videoPrivacy,
    ...analyticsProps?.videoContextFields,
  });

  const folderContext = analyticsProps?.folderContextFields
    ? buildFolderBpContext({
        folder_id: analyticsProps?.folderContextFields?.folder_id ?? 0,
        ...analyticsProps?.folderContextFields,
      })
    : {};

  const contexts = {
    ...defaultContext,
    ...productAnalyticsContext,
    ...videoContext,
    ...folderContext,
  };

  const fields = {
    posted_by_uploader: null,
    posted_by_guest: null,
    is_timecoded: null,
    is_internal: isInternal,
    value: null,
    option: null,
    comments_format: null,
  } as Fields<Events, Versions<Events>>;

  const eventName = 'vimeo.start_search_comment' as Events;

  sendBpEventWithContexts(
    eventName,
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION as Versions<Events>,
    fields,
  );
};

type SendCommentBPEventType = {
  eventName: string;
  copy: string;
  settingValue: boolean | null;
  settingOption: string | null;
  element: string | null;
  viewer?: Viewer;
  videoId?: number;
  videoOwnerUri?: string;
  videoPrivacy?: VideoPrivacy;
  modalName?: string;
  location?: string;
  comments_format?: string;
  analyticsProps?: AnalyticsPropsType;
};
export const sendCommentBPEvent = ({
  eventName,
  copy,
  settingValue,
  settingOption,
  element,
  viewer,
  videoId,
  videoOwnerUri,
  videoPrivacy = 'nobody',
  modalName,
  location = 'comments_panel',
  comments_format,
  analyticsProps,
}: SendCommentBPEventType) => {
  const webContextFields: Partial<WebBpContext> = {
    page_name: 'single_video_view_manage',
    referrer_page_name: 'single_video_view_manage',
    referrer: document.referrer,
    path: window.location.pathname,
    ...analyticsProps?.webContextFields,
  };
  const defaultContext = buildDefaultContext(webContextFields, viewer);
  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: location as 'modal' | 'comments_panel',
    element: (element as 'icon' | 'button') ?? 'dropdown',
    copy: copy,
    entity_type: 'video',
    modal_name: (modalName as 'download_comments_modal') ?? null,
    ...analyticsProps?.productContextFields,
  });
  const videoContext =
    typeof videoId === 'number'
      ? buildVideoBpContext({
          video_id: videoId,
          video_privacy: videoPrivacy,
          video_owner_id: idFromUri(videoOwnerUri ?? ''),
          ...analyticsProps?.videoContextFields,
        })
      : {};

  const folderContext = analyticsProps?.folderContextFields
    ? buildFolderBpContext({
        folder_id: analyticsProps?.folderContextFields?.folder_id ?? 0,
        ...analyticsProps?.folderContextFields,
      })
    : {};

  const contexts = {
    ...defaultContext,
    ...productAnalyticsContext,
    ...videoContext,
    ...folderContext,
  };

  sendBpEventWithContexts(
    eventName as Events,
    contexts,
    COMMENT_ACTION_GROUP_EVENT_VERSION as Versions<Events>,
    {
      posted_by_uploader: null,
      posted_by_guest: null,
      is_timecoded: null,
      is_internal: null,
      value: settingValue,
      option: settingOption,
      comments_format: comments_format ?? null,
    } as Fields<Events, Versions<Events>>,
  );
};

type SendOpenReportCommentBPEventType = {
  viewer?: Viewer;
  videoId: string;
  commentId?: string;
  isReply?: boolean;
  videoPrivacy?: VideoPrivacy;
  analyticsProps: AnalyticsPropsType;
};

export const bpOpenReportComment = ({
  viewer,
  videoId,
  commentId,
  isReply,
  videoPrivacy = 'nobody',
  analyticsProps,
}: SendOpenReportCommentBPEventType) => {
  const defaultContext = buildDefaultContext(analyticsProps.webContextFields, viewer);
  const productAnalyticsContext = buildProductAnalyticsBpContext({
    product: 'collaboration',
    feature: 'comments',
    location: 'comments_panel',
    element: 'button',
    entity_type: 'video',
  });
  const videoContext = buildVideoBpContext({
    video_id: parseInt(videoId),
    video_privacy: videoPrivacy,
  });
  const commentContext = buildDefaultCommentsContext({
    comment_id: parseInt(commentId ?? ''),
    is_reply: isReply ?? false,
    ...analyticsProps?.commentContextFields,
  });
  const thirdPartyIntegrationContext = buildDefaultThirdPartyIntegrationBpContext(
    analyticsProps?.thirdPartyContextFields,
  );

  const contexts = {
    ...defaultContext,
    ...productAnalyticsContext,
    ...videoContext,
    ...commentContext,
    ...thirdPartyIntegrationContext,
  };

  const eventName = 'vimeo.open_report_comment_modal' as Events;
  sendBpEventWithContexts(eventName, contexts);
};
